import {put, call} from 'redux-saga/effects';
import Resource from "../../entities/resource";
import * as Actions from "../actions/resourceActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ResourceAction = {
    resourceId: string;
    resource: Resource;
    resources: Resource[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneResource(action: ResourceAction) {
	const session = tabSession.captureGeneration();
	try {
		const resource: Resource = yield call(api.findOne, 'resource', action.resourceId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_FETCH_ONE_SUCCESS, resourceId: action.resourceId, resource});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.RESOURCE_FETCH_ONE_FAILURE, resourceId: action.resourceId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ResourceAction} action
 */
export function* saveResource(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ RESOURCE_SAVE saga', action.resource?.id);
    
	if (action.resource === undefined || action.resource === null) {
		console.error('saveResource received a null');
		throw new Error('saveResource received a null');
	}

	if (!(action.resource instanceof Resource)) {
		action.resource = new Resource(action.resource);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'resource', action.resource);
		if (addResult.isErr()) {
			console.error(`⮂ saveResource: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.RESOURCE_SAVE_FAILURE, resource: action.resource, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_SAVE, resource: action.resource});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.RESOURCE_SAVE_FAILURE, resource: action.resource, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ResourceAction} action
 * @param {Resource[]} action.resources
 */
export function* saveManyResources(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ RESOURCE_SAVE_MANY saga', action.resources.map(resource => resource.id).join(','));
    
    if (!Array.isArray(action.resources)) {
        console.error('saveManyResources expected an array, received', action.resources);
        throw new Error('saveManyResources expected an array');
    }

	try {
		const toSave: Resource[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.resources.length; i++) {
			const resource = action.resources[i];
			resource.persisted = false;
			toSave.push(resource);
		}

		const addResult = yield call(idb.add, 'resource', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyResources: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.RESOURCE_SAVE_MANY_FAILURE, resources: action.resources, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_SAVE_MANY, resources: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.RESOURCE_SAVE_MANY_FAILURE, resources: action.resources, status: 500, err});

		console.error(`Error saving ${action.resources.length} resources to database`, err);
	}
}

/**
 * @param {ResourceAction} action
 */
export function* removeResource(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.resource === undefined || action.resource === null || !(action.resource instanceof Resource)) {
			console.error('removeResource expected type Resource, received', action.resource);
			throw new Error('removeResource expected type Resource');
		}

	try {
		// Mark as deleted
		action.resource.status = 'D';
		action.resource.addedOn = newHRTimestamp();
		action.resource.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'resource', action.resource);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeResource: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.RESOURCE_REMOVE_FAILURE, resource: action.resource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_REMOVE, resource: action.resource});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.RESOURCE_REMOVE_FAILURE, resource: action.resource, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Resource.
	*
	* @param {ResourceAction} action
	*/
export function* restoreResource(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreResource expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.resource === undefined || action.resource === null) {
        console.error('restoreResource expected a Resource, received null');
        throw new Error('restoreResource expected a Resource, received null');
    }
    else if (!(action.resource instanceof Resource)) action.resource = new Resource(action.resource); 
    
	try {
		// Mark as restored
		action.resource.status = 'U';
		action.resource.addedOn = newHRTimestamp();
		action.resource.addedBy = userId;
		action.resource.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'resource', action.resource);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreResource: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.RESOURCE_RESTORE_FAILURE, resource: action.resource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_RESTORE, resource: action.resource});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.RESOURCE_RESTORE_FAILURE, resource: action.resource, status: 500, err});

		console.error('Error restoring from database', err);
	}
}